// https://upmostly.com/tutorials/using-custom-react-hooks-simplify-forms
// https://upmostly.com/tutorials/form-validation-using-custom-react-hooks

import React, { useState } from 'react';

interface IUseFormParameters<TValues> {
  onSubmit?: (
    values: TValues,
    errors: { [key in keyof TValues]?: any }
  ) => void;
  onValidate?: (
    values: TValues,
    errors: { [key in keyof TValues]?: any }
  ) => Record<keyof TValues, string>;
  defaultValues: TValues;
}

const useForm = function useForm<
  TValues extends { [key: string]: any } = { [key: string]: any }
>({ onSubmit, onValidate, defaultValues }: IUseFormParameters<TValues>) {
  type K = keyof TValues;
  const [values, setValues] = useState<TValues>(defaultValues || {});
  const [errors, setErrors] = useState<{ [key in K]?: any }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  function setValue(fieldName: K, value: TValues[K]) {
    setValues((tempValues) => ({
      ...tempValues,
      [fieldName]: value,
    }));
  }

  function setError(fieldName: K, value: string) {
    setErrors((tempErrors) => ({
      ...tempErrors,
      [fieldName]: value,
    }));
  }

  function handleChange(e: React.BaseSyntheticEvent) {
    e.persist();
    setValues((tempValues) => ({
      ...tempValues,
      [e.target.name]: e.target.value,
    }));
  }

  async function handleValidation(e?: string | React.BaseSyntheticEvent) {
    let fieldName: string;
    let newErrors: { [key in K]?: any } = {};

    if (e && typeof e !== 'string' && e.target) {
      e.persist();
      fieldName = e.target.name;
    } else {
      fieldName = e as string;
    }

    if (onValidate) {
      newErrors = await onValidate(values, errors);

      setErrors((tempErrors) => {
        // Only add error to certain field,
        // and keeping the error of other field,
        // and remove error if other field already valid
        if (fieldName) {
          const fieldError = { [fieldName]: newErrors[fieldName] };
          const oldErrorKeys = Object.keys(tempErrors);
          const newErrorKeys = Object.keys(newErrors);
          // compared error and get keys that already valid
          const validKeys = oldErrorKeys.filter(
            (key) => !newErrorKeys.includes(key)
          );

          // remove error that already valid
          validKeys.forEach((key) => {
            delete tempErrors[key];
          });

          newErrors = {
            ...tempErrors,
            ...fieldError,
          };
        }
        return newErrors;
      });
    }

    return newErrors;
  }

  async function handleSubmit(e?: React.BaseSyntheticEvent) {
    if (e) {
      e.preventDefault();
    }

    const newErrors = await handleValidation();

    if (Object.keys(newErrors).length === 0 && !isSubmitting && onSubmit) {
      onSubmit(values, errors);
    }
  }

  return {
    values,
    setValues,
    setValue,
    handleChange,
    handleSubmit,
    errors,
    setErrors,
    setError,
    handleValidation,
    isSubmitting,
    setIsSubmitting,
  };
};

export default useForm;
